import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import Text from '@/components/common/typeface/Text'
import { removeScrollbar } from '@/utils/utilCSS'

export interface TabType {
  tabId: string
  tabName: string
  index: number
}

interface Props {
  activeTabIndex: number
  tabs?: {
    id: string
    tabName: string
  }[]
  onClickTab: (value: TabType) => void
}

/**
 * @summary Controlled Component이기 때문에 부모 컴포넌트에서 tab index 상태를 관리해야함
 * @param activeTabIndex - 활성화된 tab index
 * @param tabs - tab 데이터
 * @param onClickTab - tab 클릭시 실행되는 함수
 */
const HorizontalScrollTabs = ({ tabs, onClickTab, activeTabIndex }: Props) => {
  const tabsRef = useRef<Map<number, HTMLElement>>(new Map())

  const getMap = () => {
    if (!tabsRef.current) {
      tabsRef.current = new Map()
    }

    return tabsRef.current
  }

  useEffect(() => {
    if (window.scrollY === 0) return
    const map = getMap()
    const node = map.get(activeTabIndex)
    node?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    })
  }, [activeTabIndex])

  const handleTabClick = (tabId: string, tabName: string, index: number) => {
    onClickTab({ tabId, tabName, index })
  }

  return (
    <StyledTabContainer>
      {tabs?.map((item, index: number) => {
        if (!item.id) return null
        return (
          <li
            key={item.id}
            ref={(node) => {
              const map = getMap()
              if (node) {
                map.set(index, node)
              } else {
                map.delete(index)
              }
            }}
            role="tab"
            className={activeTabIndex === index ? 'active' : ''}
            onClick={() => handleTabClick(item.id, item.tabName, index)}
          >
            <StyledTab>{item.tabName}</StyledTab>
          </li>
        )
      })}
    </StyledTabContainer>
  )
}

const StyledTab = styled(Text.Body2)`
  padding: 1.2rem 1.6rem;
  white-space: nowrap;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  color: ${(props) => props.theme.color.gray500};
`

const StyledTabContainer = styled.ul`
  ${removeScrollbar};

  display: flex;
  justify-content: flex-start;
  overflow-x: scroll;
  cursor: pointer;
  background-color: ${(props) => props.theme.color.gray900};
  margin-bottom: 0;

  .active > * {
    font-weight: ${(props) => props.theme.fontWeight.bold};
    color: ${(props) => props.theme.color.grayWhite};
  }
`

export default HorizontalScrollTabs
